import React, { useState } from 'react';
import { 
  Box, 
  ListItem, 
  Typography, 
  Paper, 
  Grid, 
  Chip,
  Card,
  CardContent,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Collapse,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  EmojiEvents,
  Timeline,
  ArrowUpward,
  ArrowDownward,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

// Helper function to format large numbers
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined) return '-';
  if (typeof num === 'number') {
    return num.toFixed(decimals);
  }
  return '-';
};

// Helper function to format currency
const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(num);
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

// Component to render AI labels
const AILabelsSection = ({ aiLabels }) => {
  if (!aiLabels || aiLabels.length === 0) return null;

  const getLabelColor = (confidence) => {
    switch (confidence) {
      case 'high':
        return 'secondary'; // Purple
      case 'med':
        return 'error'; // Red  
      case 'low':
        return 'warning'; // Orange
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        AI Labels
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {aiLabels.map((label) => (
          <Chip
            key={label.id}
            label={label.id}
            color={getLabelColor(label.confidence)}
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    </Box>
  );
};

// Component to render tags
const TagsSection = ({ tags }) => {
  if (!tags || tags.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Tags
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {tags.map((tag, idx) => (
          <Chip
            key={idx}
            label={tag}
            size="small"
            variant="outlined"
          />
        ))}
      </Box>
    </Box>
  );
};

// Component to render market info header
const MarketInfoSection = ({ market, hit }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            {market.question || 'No question'}
          </Typography>
          {market.description && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1 }}>
              "{market.description}"
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={hit._score != null ? hit._score.toFixed(2) : '-'}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {market.icon && (
          <Avatar
            alt="Market icon"
            src={market.icon}
            sx={{
              width: 32,
              height: 32,
              backgroundColor: 'grey.200',
              border: '2px solid',
              borderColor: 'primary.main'
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        {market.sports_market_type && (
          <Chip 
            label={market.sports_market_type}
            size="small"
            variant="filled"
            color="info"
          />
        )}
        {market.featured && (
          <Chip 
            label="Featured"
            size="small"
            variant="filled"
            color="warning"
          />
        )}
        {market.restricted && (
          <Chip 
            label="Restricted"
            size="small"
            variant="filled"
            color="error"
          />
        )}
        {market.closed && (
          <Chip 
            label="Closed"
            size="small"
            variant="filled"
            color="error"
          />
        )}
        {!market.closed && (
          <Chip 
            label="Open"
            size="small"
            variant="filled"
            color="success"
          />
        )}
        {market.funded && (
          <Chip 
            label="Funded"
            size="small"
            variant="filled"
            color="success"
          />
        )}
        {market.neg_risk && (
          <Chip 
            label="Neg Risk"
            size="small"
            variant="filled"
            color="success"
          />
        )}
        {market.fees_enabled && (
          <Chip 
            label="Fees Enabled"
            size="small"
            variant="filled"
            color="success"
          />
        )}
      </Box>
    </Box>
  );
};

// Component to render market info with pricing, volume, and price changes
const MarketMetricsSection = ({ market }) => {
  const getPriceChangeColor = (value) => {
    if (value === null || value === undefined) return 'text.primary';
    return value >= 0 ? '#2e7d32' : '#d32f2f';
  };

  // Pricing table data - one row per outcome
  const pricingTableRows = [];
  if (market.outcomes && market.outcome_prices) {
    market.outcomes.forEach((outcome, idx) => {
      pricingTableRows.push({
        outcome,
        price: formatCurrency(market.outcome_prices[idx])
      });
    });
  }

  // Volume table data
  const volumeTableData = [
    { label: 'Volume (All)', value: formatCurrency(market.volume) },
    { label: 'Volume (24h)', value: formatCurrency(market.volume_24hr) },
    { label: 'Volume (W)', value: formatCurrency(market.volume_1wk) },
    { label: 'Volume (M)', value: formatCurrency(market.volume_1mo) },
    { label: 'Best Ask', value: formatCurrency(market.best_ask) },
    { label: 'Liquidity', value: formatCurrency(market.liquidity) },
    { label: 'Spread', value: formatNumber(market.spread, 2) },
  ];

  // Price changes table data
  const priceChangesTableData = [
    { label: 'Hour', value: market.one_hour_price_change },
    { label: 'Day', value: market.one_day_price_change },
    { label: 'Week', value: market.one_week_price_change },
    { label: 'Month', value: market.one_month_price_change },
    { label: 'Year', value: market.one_year_price_change }
  ];

  // Determine price change indicator
  const hourlyChange = market.one_hour_price_change;
  let priceChangeIcon = null;
  if (hourlyChange !== null && hourlyChange !== undefined) {
    if (hourlyChange > 0.01) {
      priceChangeIcon = <ArrowUpward sx={{ fontSize: '1.2rem', color: '#2e7d32', mr: 0.5 }} />;
    } else if (hourlyChange < -0.01) {
      priceChangeIcon = <ArrowDownward sx={{ fontSize: '1.2rem', color: '#d32f2f', mr: 0.5 }} />;
    }
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Market Info
      </Typography>
      <Grid container spacing={2} sx={{ width: '100%' }}>
        {/* Outcomes Table */}
        <Grid sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <EmojiEvents sx={{ fontSize: '1.2rem', mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Outcomes
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200', flex: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Outcome</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricingTableRows.length > 0 ? (
                  pricingTableRows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontSize: '0.85rem', p: 0.75 }}>{row.outcome}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.85rem', p: 0.75 }}>{row.price}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontSize: '0.85rem', p: 0.75, textAlign: 'center' }}>
                      No pricing data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Volume Table */}
        <Grid sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Timeline sx={{ fontSize: '1.2rem', mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Volume
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200', flex: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Metric</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {volumeTableData.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontSize: '0.85rem', p: 0.75 }}>{row.label}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.85rem', p: 0.75 }}>{row.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Price Changes Table */}
        <Grid sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {priceChangeIcon}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Price
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200', flex: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Period</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {priceChangesTableData.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontSize: '0.85rem', p: 0.75 }}>{row.label}</TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontSize: '0.85rem', 
                        p: 0.75,
                        color: getPriceChangeColor(row.value)
                      }}
                    >
                      {formatNumber(row.value, 2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

// Component to render dates
const DatesSection = ({ market }) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Dates
      </Typography>
      <Grid container spacing={1}>
        {market.created_at && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Created
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(market.created_at)}
              </Typography>
            </Paper>
          </Grid>
        )}
        {market.start_date && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Start Date
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(market.start_date)}
              </Typography>
            </Paper>
          </Grid>
        )}
        {market.end_date && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                End Date
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(market.end_date)}
              </Typography>
            </Paper>
          </Grid>
        )}
        {market.game_start_time && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Game Start Time
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(market.game_start_time)}
              </Typography>
            </Paper>
          </Grid>
        )}
        {market.updated_at && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Updated
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatDate(market.updated_at)}
              </Typography>
            </Paper>
          </Grid>
        )}
        {market.last_trade_price !== null && market.last_trade_price !== undefined && (
          <Grid>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                backgroundColor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Last Trade Price
              </Typography>
              <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>
                {formatCurrency(market.last_trade_price)}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// Helper function to get user display name and icon
const getUserDisplay = (bet) => {
  const displayName = bet.name ||bet.user_name || bet.pseudonym || bet.user_id || '?';
  const pfp = bet.pfp || bet.user_pfp;
  return { displayName, pfp };
};

// Component to render a single bet row
const BetRow = ({ bet, isSimilarWallet = false, idx = 0 }) => {
  const getSideLabel = (side) => {
    if (side === 'BUY') return 'bought';
    if (side === 'SELL') return 'sold';
    if (side === 'REDEEM') return 'redeemed';
    return side?.toLowerCase() || 'traded';
  };

  const getOutcomeColor = (outcome) => {
    const colors = {
      'Yes': '#4caf50',
      'No': '#f44336'
    };
    return colors[outcome] || '#2196f3';
  };

  const isSmartMoney = (bet) => {
    if (!bet.user_pnl || !bet.user_volume || bet.user_volume === 0) return false;
    return (bet.user_pnl / bet.user_volume) > 0.2;
  };

  const isWhale = (bet) => {
    return bet.user_volume > 10000;
  };

  const { displayName, pfp } = getUserDisplay(bet);

  return (
    <Paper
      key={bet.id || bet.user_id || idx}
      sx={{
        p: 1.5,
        mb: 1,
        backgroundColor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1
      }}
    >
      {/* Left side - User info and trade details */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {/* Avatar */}
        {pfp ? (
          <Avatar
            src={pfp}
            alt={displayName}
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: 'primary.main',
              flexShrink: 0,
              fontSize: '0.875rem'
            }}
          >
            {displayName?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
        )}

        {/* Trade details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="body2" 
            sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={displayName}
          >
            {displayName}
          </Typography>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
          >
            {getSideLabel(bet.side)} {bet.shares} shares{bet.outcome ? ` of ${bet.outcome}` : ''} at ${formatNumber(bet.price, 2)}
          </Typography>
        </Box>
      </Box>

      {/* Right side - Chips */}
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
        {/* Similarity chip (for similar wallets only) - shown first */}
        {isSimilarWallet && bet.similarity && (
          <Chip
            label={`Wallet similarity ${(bet.similarity * 100).toFixed(0)}%`}
            size="small"
            variant="outlined"
            color="secondary"
          />
        )}

        {/* Price chip */}
        <Chip
          label={formatCurrency(bet.usdc)}
          size="small"
          variant="outlined"
          color="primary"
        />

        {/* Outcome chip */}
        {bet.outcome && (
          <Chip
            label={bet.outcome}
            size="small"
            variant="filled"
            sx={{ backgroundColor: getOutcomeColor(bet.outcome), color: '#fff' }}
          />
        )}

        {/* Smart Money chip */}
        {isSmartMoney(bet) && (
          <Chip
            label="Smart 💡"
            size="small"
            variant="filled"
            color="success"
          />
        )}

        {/* Whale chip */}
        {isWhale(bet) && (
          <Chip
            label="Whale 🐋"
            size="small"
            variant="filled"
            color="info"
          />
        )}
      </Box>
    </Paper>
  );
};

// Component to render similar wallets bets
const SimilarWalletsBets = ({ similarWalletsBets, outcomes }) => {
  const [expanded, setExpanded] = useState(false);

  if (!similarWalletsBets || similarWalletsBets.length === 0) return null;

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  // Calculate outcome percentages weighted by USDC amount (only BUY trades)
  const outcomeUSDC = {};
  let totalUSDC = 0;

  similarWalletsBets.forEach((bet) => {
    // Only count BUY trades with an outcome, ignore missing outcomes and other trade types
    if (bet.outcome && bet.side === 'BUY') {
      const usdc = bet.usdc || 0;
      if (!outcomeUSDC[bet.outcome]) {
        outcomeUSDC[bet.outcome] = 0;
      }
      outcomeUSDC[bet.outcome] += usdc;
      totalUSDC += usdc;
    }
  });

  // Calculate percentages
  const outcomePercentages = {};
  const outcomeKeys = Object.keys(outcomeUSDC).sort((a, b) => outcomeUSDC[b] - outcomeUSDC[a]);
  
  outcomeKeys.forEach((outcome) => {
    outcomePercentages[outcome] = totalUSDC > 0 ? (outcomeUSDC[outcome] / totalUSDC) * 100 : 0;
  });

  // Color palette for outcomes
  const getOutcomeColor = (outcome) => {
    const colors = {
      'Yes': '#4caf50',
      'No': '#f44336'
    };
    return colors[outcome] || '#2196f3';
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {similarWalletsBets.length} similar users bets
        </Typography>

        {/* Outcome percentage bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 0.4 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {outcomeKeys.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {outcomeKeys.map((outcome) => {
                  const percentage = outcomePercentages[outcome] || 0;
                  return (
                    <Box key={outcome} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 40 }}>
                        {outcome}
                      </Typography>
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getOutcomeColor(outcome),
                              borderRadius: 4
                            }
                          }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right' }}>
                          {percentage.toFixed(0)}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Expand/Collapse button */}
          <IconButton
            onClick={handleToggle}
            sx={{ p: 0.5, flexShrink: 0 }}
            size="small"
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1 }}>
          {similarWalletsBets.map((bet, idx) => (
            <BetRow key={idx} bet={bet} isSimilarWallet={true} idx={idx} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

// Component to render bets
const BetsSection = ({ bets }) => {
  const [expanded, setExpanded] = useState(false);

  if (!bets || bets.length === 0) return null;

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Significant Bets ({bets.length})
        </Typography>
        <IconButton
          onClick={handleToggle}
          sx={{ p: 0.5 }}
          size="small"
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1 }}>
          {bets.map((bet, idx) => (
            <BetRow key={idx} bet={bet} isSimilarWallet={false} idx={idx} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const toTextArray = (v) => {
  if (v == null || v === '') return [];
  return Array.isArray(v) ? v : [v];
};

// Component to render market ID and event links
const DocumentInfoSection = ({ hit, market }) => {
  const marketId = market.item_id || hit._id;
  const slug = market.slug || hit._source?.slug || "";
  const marketUrl = slug ? `https://polymarket.com/market/${slug}` : null;

  const eventIds = toTextArray(market.event_ids ?? hit._source?.event_ids);
  const eventSlugs = toTextArray(market.event_slugs ?? hit._source?.event_slugs);
  const nEvents = Math.max(eventIds.length, eventSlugs.length);
  const eventRows = [];
  for (let i = 0; i < nEvents; i++) {
    const eventId = eventIds[i];
    const eventSlug = eventSlugs[i];
    const hasId = eventId != null && eventId !== '';
    const hasSlug = eventSlug != null && eventSlug !== '';
    if (!hasId && !hasSlug) continue;
    eventRows.push({ eventId, eventSlug, hasId, hasSlug });
  }

  const captionSx = {
    color: 'text.secondary',
    wordBreak: 'break-all',
    fontSize: '0.7rem'
  };

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={captionSx}>
          {marketUrl ? (
            <Link
              href={marketUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Market#{marketId}
              {slug ? ` (${slug})` : ''}
            </Link>
          ) : (
            <>
              Market#{marketId}
              &nbsp;
              {slug ? `(${slug})` : ''}
            </>
          )}
        </Typography>
        {eventRows.map(({ eventId, eventSlug, hasId, hasSlug }, i) => {
          const eventUrl = hasSlug ? `https://polymarket.com/event/${eventSlug}` : null;
          const label =
            (hasId ? `Event#${eventId}` : 'Event') + (hasSlug ? ` (${eventSlug})` : '');
          return (
            <Typography key={i} variant="caption" sx={captionSx}>
              {eventUrl ? (
                <Link
                  href={eventUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  {label}
                </Link>
              ) : (
                label
              )}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
};

const PolymarketItemCard = ({ hit }) => {
  const { _source: market } = hit;
  
  // Build AI labels
  const aiLabels_high = market.ai_labels_high || [];
  const aiLabels_med = market.ai_labels_med || [];
  const aiLabels_low = market.ai_labels_low || [];
  const aiLabelsMap = {};
  
  for (const label of aiLabels_high) {
    aiLabelsMap[label] = 'high';
  }
  for (const label of aiLabels_med) {
    if (!(label in aiLabelsMap)) {
      aiLabelsMap[label] = 'med';
    }
  }
  for (const label of aiLabels_low) {
    if (!(label in aiLabelsMap)) {
      aiLabelsMap[label] = 'low';
    }
  }
  const aiLabels = Object.keys(aiLabelsMap).map((label) => ({
    id: label,
    confidence: aiLabelsMap[label]
  }));

  return (
    <ListItem 
      sx={{ 
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0
      }}
    >
      <Card
        elevation={1}
        sx={{
          width: '100%',
          backgroundColor: '#fafafa',
          overflow: 'visible'
        }}
      >
        {market.image && (
          <Box
            component="img"
            src={market.image}
            alt="Market cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            sx={{
              width: '100%',
              height: 200,
              objectFit: 'cover',
              display: 'block'
            }}
          />
        )}
        <CardContent sx={{ pb: 2 }}>
          {/* Market Info */}
          <MarketInfoSection market={market} hit={hit} />

          {/* AI Labels */}
          <AILabelsSection aiLabels={aiLabels} />

          {/* Tags */}
          <TagsSection tags={market.tags} />

          {/* Market Metrics (Pricing, Volume, Price Changes) */}
          <MarketMetricsSection market={market} />

          {/* Dates */}
          <DatesSection market={market} />

          {/* Similar Wallets Bets */}
          <SimilarWalletsBets similarWalletsBets={hit._info?.similar_wallets} outcomes={market.outcomes} />

          {/* Bets */}
          <BetsSection bets={hit._info?.bets} />

          {/* Market Details */}
          <DocumentInfoSection hit={hit} market={market} />
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default PolymarketItemCard;

